import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Layout } from './components/layout';
import { useAuth } from './lib/auth';
import { LandingPage } from './pages/landing';
import { SignInPage } from './pages/sign-in';
import { HomePage } from './pages/home';
import { JoinPage, JoinWithCodePage } from './pages/join';
import { AccountLayout } from './pages/account/layout';
import { AccountProfilePage } from './pages/account/profile';
import { AccountSecurityPage } from './pages/account/security';
import { AccountAppearancePage } from './pages/account/appearance';
import { NewOrganizationPage } from './pages/organizations/new';
import { OrgLayout } from './pages/organizations/layout';
import { OrgOverviewPage } from './pages/organizations/overview';
import { OrgMembersPage } from './pages/organizations/members';
import { OrgInvitesPage } from './pages/organizations/invites';
import { OrgChildrenPage } from './pages/organizations/children';
import { NewChildOrgPage } from './pages/organizations/new-child';
import { OrgSettingsPage } from './pages/organizations/settings';
import { AdminLayout } from './pages/admin/layout';
import { AdminOverviewPage } from './pages/admin/overview';
import { AdminRequestsPage } from './pages/admin/requests';
import { AdminOrgsPage } from './pages/admin/orgs';
import { AdminNewOrgPage } from './pages/admin/new-org';
import { AdminUsersPage } from './pages/admin/users';
import { AdminAuditPage } from './pages/admin/audit';
import {
  AdminLibraryBankFormPage,
  AdminLibraryBankPage,
  AdminLibraryBanksPage,
  AdminLibraryNewQuizPage,
  AdminLibraryPage,
  AdminLibraryQuestionEditorPage,
} from './pages/admin/library';
import {
  OrgNewQuizPage,
  OrgQuestionBankFormPage,
  OrgQuestionBankPage,
  OrgQuestionBanksPage,
  OrgQuestionEditorPage,
  OrgQuizzesPage,
} from './pages/organizations/quizzes';
import { OrgReportsPage } from './pages/organizations/reports';
import { QuizEditorPage } from './components/quiz/authoring';
import { TakeQuizPage } from './pages/quiz/take';
import { QuizPosterPage } from './pages/quiz/poster';
import { AttemptResultPage } from './pages/quiz/result';
import { CertificatesPage } from './pages/certificates';
import { VerifyCertificatePage } from './pages/verify';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return user ? children : <Navigate to="/signin" replace />;
}

/** Signed-in people land on their dashboard; everyone else sees the landing page. */
function Root() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/home" replace /> : <LandingPage />;
}

/** Old `/orgs/...` links (e.g. in earlier emails) keep working. */
function LegacyOrgRedirect() {
  const { '*': rest = '' } = useParams();
  return <Navigate to={`/organizations/${rest}`} replace />;
}

const authed = (el: React.ReactNode) => <RequireAuth>{el}</RequireAuth>;

export function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Root />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/home" element={authed(<HomePage />)} />
        <Route path="/join" element={authed(<JoinPage />)} />
        <Route path="/join/:code" element={<JoinWithCodePage />} />
        <Route path="/account" element={authed(<AccountLayout />)}>
          <Route index element={<AccountProfilePage />} />
          <Route path="security" element={<AccountSecurityPage />} />
          <Route path="appearance" element={<AccountAppearancePage />} />
        </Route>
        <Route path="/certificates" element={authed(<CertificatesPage />)} />

        {/* Public: a quiz's intro (sign in to start) and certificate verification. */}
        <Route path="/q/:slug" element={<TakeQuizPage />} />
        <Route path="/q/:slug/poster" element={<QuizPosterPage />} />
        <Route path="/attempts/:attemptId" element={authed(<AttemptResultPage />)} />
        <Route path="/verify/:code" element={<VerifyCertificatePage />} />
        <Route path="/quizzes/:quizId/edit" element={authed(<QuizEditorPage />)} />

        <Route path="/organizations" element={<Navigate to="/home" replace />} />
        <Route path="/organizations/new" element={authed(<NewOrganizationPage />)} />
        <Route path="/organizations/:orgId" element={authed(<OrgLayout />)}>
          <Route index element={<OrgOverviewPage />} />
          <Route path="members" element={<OrgMembersPage />} />
          <Route path="invites" element={<OrgInvitesPage />} />
          <Route path="children" element={<OrgChildrenPage />} />
          <Route path="children/new" element={<NewChildOrgPage />} />
          <Route path="settings" element={<OrgSettingsPage />} />
          <Route path="quizzes" element={<OrgQuizzesPage />} />
          <Route path="quizzes/new" element={<OrgNewQuizPage />} />
          <Route path="question-banks" element={<OrgQuestionBanksPage />} />
          <Route path="question-banks/new" element={<OrgQuestionBankFormPage />} />
          <Route path="question-banks/:bankId" element={<OrgQuestionBankPage />} />
          <Route path="question-banks/:bankId/edit" element={<OrgQuestionBankFormPage />} />
          <Route path="question-banks/:bankId/questions/new" element={<OrgQuestionEditorPage />} />
          <Route
            path="question-banks/:bankId/questions/:questionId"
            element={<OrgQuestionEditorPage />}
          />
          <Route path="reports" element={<OrgReportsPage />} />
        </Route>
        <Route path="/orgs/*" element={<LegacyOrgRedirect />} />

        <Route path="/admin" element={authed(<AdminLayout />)}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="requests" element={<AdminRequestsPage />} />
          <Route path="organizations" element={<AdminOrgsPage />} />
          <Route path="organizations/new" element={<AdminNewOrgPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
          <Route path="library" element={<AdminLibraryPage />} />
          <Route path="library/quizzes/new" element={<AdminLibraryNewQuizPage />} />
          <Route path="library/question-banks" element={<AdminLibraryBanksPage />} />
          <Route path="library/question-banks/new" element={<AdminLibraryBankFormPage />} />
          <Route path="library/question-banks/:bankId" element={<AdminLibraryBankPage />} />
          <Route
            path="library/question-banks/:bankId/edit"
            element={<AdminLibraryBankFormPage />}
          />
          <Route
            path="library/question-banks/:bankId/questions/new"
            element={<AdminLibraryQuestionEditorPage />}
          />
          <Route
            path="library/question-banks/:bankId/questions/:questionId"
            element={<AdminLibraryQuestionEditorPage />}
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
